import clsx from 'clsx';
import { MouseEvent, useCallback, useEffect, useState } from 'react';

import { Button } from '~components/generic/editor/Button/Button';
import { UserSearchAutocomplete } from '~components/generic/UserSearchAutocomplete/UserSearchAutocomplete';
import { CaretDownIcon, CloseIcon, MoreIcon, TeamIcon, UserAddIcon, WarningIcon } from '~components/icons';
import { ApiErrorDescriptor } from '~services/api/helpers';
import {
  useAddUserToUsergroup,
  useDeleteUsergroup,
  useRenameUsergroup,
  useUsergroup,
} from '~services/api/hooks/user-management.hooks';
import { AddUsersModal } from './AddUsersModal';
import { ContextMenu } from './ContextMenu';
import { UsersList } from './UsersList';
import styles from './UsergroupsList.module.css';

export type UsergroupsListItemProps = {
  usergroup: UsergroupShape;
};

export const UsergroupsListItem = ({ usergroup: propUsergroup }: UsergroupsListItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [doRename, setDoRename] = useState(false);
  const [doDelete, setDoDelete] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState<{ pageX: number; pageY: number }>(undefined);
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>(undefined);

  // Define: reset states
  const resetState = useCallback(() => {
    setIsProcessing(false);
    setDoRename(false);
    setDoDelete(false);
    setShowContextMenu(undefined);
  }, []);

  // Define: fetch usergroup query
  const {
    isLoading,
    data: usergroup,
    error: groupError,
    isSuccess,
  } = useUsergroup(propUsergroup.name, {
    placeholderData: propUsergroup,
  });
  useEffect(() => {
    resetState();
    if (groupError) setError(groupError.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupError]);
  useEffect(() => {
    resetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  // Define: delete usergroup query
  const { mutate: removeUsergroup } = useDeleteUsergroup({
    onSuccess: () => {
      setIsDeleted(true);
    },
    onError: (e: ApiErrorDescriptor) => {
      setError(e.error);
    },
    onSettled: resetState,
  });

  // Define: patch usergroup name query
  const { mutate: patchUsergroupName } = useRenameUsergroup({
    onError: (e: ApiErrorDescriptor) => {
      setError(e.error);
    },
    onSettled: resetState,
  });

  // Define: patch usergroup add user query
  const { mutate: patchUsergroupAddUser } = useAddUserToUsergroup({
    onError: (e: ApiErrorDescriptor) => {
      setError(e.error);
    },
    onSettled: resetState,
  });

  // Trigger: delete usergroup when doDelete recieved
  useEffect(() => {
    if (doDelete) {
      setIsExpanded(false);
      setIsProcessing(true);
      void removeUsergroup(usergroup.name);
    }
  }, [removeUsergroup, doDelete, usergroup.name]);

  // Handler: Show contextmenu on click
  const handleOnSettingsButtonClick = useCallback((e: MouseEvent) => {
    const rect = (e.target as HTMLButtonElement).getBoundingClientRect();
    setShowContextMenu({
      pageX: rect.right,
      pageY: rect.bottom,
    });
  }, []);

  // Handler: Toggle expand on click
  const handleOnNameClick = useCallback(
    () => !isDeleted && !isProcessing && setIsExpanded(!isExpanded),
    [isDeleted, isProcessing, isExpanded],
  );

  // Handle: Add user to group
  const handleAddUser = useCallback(
    (userName: string) => {
      // Check if user is NOT already in userlist
      if (!(usergroup.users || []).includes(userName)) {
        setIsProcessing(true);
        void patchUsergroupAddUser({ groupName: usergroup.name, userName });
      }
    },
    [patchUsergroupAddUser, usergroup],
  );

  // Handler: Abort renaming
  const handleAbortRenameUsergroup = useCallback(() => setDoRename(false), []);

  // Hanlder: Process renaming -> send new Name to API
  const handleRenameUsergroup = useCallback(
    (newName: string) => {
      setIsProcessing(true);
      void patchUsergroupName({ groupName: usergroup.name, newGroupName: newName });
    },
    [patchUsergroupName, usergroup.name],
  );

  // Handler: remove error message
  const handleOnErrorClick = useCallback(() => setError(undefined), []);

  if (error) {
    return (
      <div className={clsx(styles.listItem, styles.overlayError)} title={error}>
        <div className={styles.listItemSummary}>
          <div className={styles.cellName}>
            <span className={styles.cellNameIcon}>
              <WarningIcon />
            </span>
            <span className={styles.cellNameLabel}>{error}</span>
          </div>
          <div className={styles.cellAction}>
            <Button onClick={handleOnErrorClick} unflashy icon={<CloseIcon />} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={clsx(styles.listItem, isDeleted && styles.listItemDeleted)}>
        <div className={styles.listItemSummary}>
          <div
            className={clsx(styles.cellName, styles.cellNameExpandable, isExpanded && styles.cellNameExpanded)}
            onClick={handleOnNameClick}
          >
            <span className={styles.cellNameIcon}>
              <TeamIcon />
            </span>
            <span className={styles.cellNameLabel}>
              {doRename ? (
                <input
                  autoFocus={true}
                  defaultValue={usergroup.name}
                  onBlur={event => {
                    const title = (event.target.value || '').trim();
                    if (title === '' || title === usergroup.name) {
                      handleAbortRenameUsergroup();
                    } else {
                      handleRenameUsergroup(title);
                    }
                  }}
                  onFocus={event => {
                    // auto select all text if unchanged
                    if (event.target.value === usergroup.name) {
                      event.target.setSelectionRange(0, event.target.value.length);
                    }
                  }}
                  onKeyPress={event => {
                    if (event.key === 'Enter') {
                      if (document.activeElement instanceof HTMLElement) {
                        document.activeElement.blur();
                      }
                    }
                  }}
                  onClick={event => {
                    event.stopPropagation();
                  }}
                  disabled={isProcessing}
                />
              ) : (
                usergroup.name
              )}
            </span>
            <span className={styles.cellNameExpandIcon}>
              <CaretDownIcon />
            </span>
          </div>
          <div className={styles.cellAction}>
            <Button
              processing={isProcessing}
              disabled={isDeleted}
              onClick={handleOnSettingsButtonClick}
              unflashy
              icon={<MoreIcon />}
            />
          </div>
        </div>
        {isExpanded && (
          <div className={styles.listItemDetails}>
            <UsersList groupName={usergroup.name} users={usergroup.users} loading={isLoading} />
            <div className={styles.listItemNew}>
              <UserSearchAutocomplete
                onSelect={handleAddUser}
                icon={<UserAddIcon />}
                placeholderText="placeholderAddUser"
              />
            </div>
          </div>
        )}
      </div>
      {showContextMenu && (
        <ContextMenu
          onClose={() => setShowContextMenu(undefined)}
          onClickRename={() => setDoRename(true)}
          onClickDelete={() => setDoDelete(true)}
          onClickAddUsers={() => setShowAddUsersModal(true)}
          pageX={showContextMenu.pageX}
          pageY={showContextMenu.pageY}
          groupId={usergroup.uuid}
        />
      )}
      <AddUsersModal isOpen={showAddUsersModal} usergroup={usergroup} onClose={() => setShowAddUsersModal(false)} />
    </>
  );
};
